import { useCallback, useEffect, useState } from "react";
import { useLoadingModal } from "../../../components/primitives/feedback/LoadingModal";
import { useAnnotationFieldTimer } from "../../../hooks/useAnnotationFieldTimer";
import { SCORECARD, type ScorecardKey } from "@shared/scorecard";
import {
  getDocument,
  getDocumentMarkdown,
  getEffectiveAiLabels,
  getEffectiveScorecard,
  saveAnnotation,
  type AnnotationDimension,
  type DimensionReviewState,
  type ExtractedScorecardWithEvidence,
  type OverrideInteraction,
  type QueueRow,
} from "../../../lib/corpusApi";
import { fieldStatus } from "../../../lib/scorecardFieldStatus";

interface FieldGain {
  field: ScorecardKey;
  gain: number;
}

/** T2/T3 fields still needing a human look — either never extracted, or extracted below their
 *  tier's AUTO_VALIDATE_THRESHOLD (see fieldStatus) — sorted by potential point contribution
 *  descending (field order here is gain, not tier). Scoped to T2/T3 only — T1 fields are the
 *  primary backlog's job (AnnotationReview), not Improve Ranking's; a document reaching this view
 *  already cleared its T1 gate. A field the annotator has already recorded any decision for, even
 *  "mark empty", is excluded — that decision is final, not a gap to keep refilling. Mirrors the
 *  backend's fieldPointGains (internal/scorecard_scoring.ts) against data this view already has,
 *  rather than a second round-trip for the same document. */
function computeFieldGains(
  aiScorecard: ExtractedScorecardWithEvidence | null,
  annotation: QueueRow["annotation"]
): FieldGain[] {
  if (!aiScorecard) return [];
  const gains: FieldGain[] = [];
  for (const key of Object.keys(SCORECARD) as ScorecardKey[]) {
    if (SCORECARD[key].tier === 1) continue;
    if (fieldStatus(key, aiScorecard, annotation) !== "needs_review") continue;
    const values = Object.values(SCORECARD[key].scoring);
    const gain = values.length > 0 ? Math.max(...values) : 0;
    if (gain > 0) gains.push({ field: key, gain });
  }
  return gains.sort((a, b) => b.gain - a.gain);
}

function reviewState(aiLabels: string[], selection: string[]): DimensionReviewState {
  if (aiLabels.length === 0) return selection.length === 0 ? "empty" : "human_filled";
  return "ai_overridden";
}

export interface UseImproveDocumentResult {
  document: QueueRow | null;
  field: ScorecardKey | null;
  remaining: number;
  loading: boolean;
  error: string | null;
  saving: boolean;
  savedMessage: string | null;
  override: OverrideInteraction;
  openOverride: () => void;
  approve: () => void;
  markEmpty: () => void;
}

/** Drives the Improve Ranking per-document view (TAN-37): works through this one document's
 *  gain-ordered fields, refetching after each save so the next-highest-gain field becomes
 *  current automatically (no separate "advance" bookkeeping — a saved field simply stops
 *  appearing in the recomputed gain list). No auto-ingest concern: the document is already
 *  ingested; saving here only refreshes its scorecard/metadata. */
export function useImproveDocument(sourceId: string): UseImproveDocumentResult {
  const { withLoadingModal, loading: saving } = useLoadingModal();

  const [document, setDocument] = useState<QueueRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  const [overrideMode, setOverrideModeState] = useState(false);
  const [overrideFocusIndex, setOverrideFocusIndex] = useState(0);
  const [overrideSelection, setOverrideSelection] = useState<string[]>([]);

  const effectiveScorecard = getEffectiveScorecard(document?.ai_scorecard ?? null);
  const gains = document ? computeFieldGains(effectiveScorecard, document.annotation) : [];
  const field = gains[0]?.field ?? null;

  const loadDocument = useCallback(async () => {
    setLoading(true);
    setError(null);
    setOverrideModeState(false);
    try {
      const [doc, markdown_text] = await Promise.all([
        getDocument(sourceId),
        getDocumentMarkdown(sourceId),
      ]);
      setDocument({ ...doc, markdown_text });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load the document");
    } finally {
      setLoading(false);
    }
  }, [sourceId]);

  useEffect(() => {
    void loadDocument();
  }, [loadDocument]);

  const timer = useAnnotationFieldTimer({
    docUrl: document?.url ?? null,
    field,
    loading,
  });

  const commit = useCallback(
    async (labels: string[], state: DimensionReviewState) => {
      const doc = document;
      const key = field;
      if (!doc || !key) return;

      timer.record(labels, state);

      setError(null);
      try {
        await withLoadingModal(async () => {
          const entry: AnnotationDimension = { labels, state };
          const merged = { ...(doc.annotation ?? {}), [key]: entry };
          await saveAnnotation(doc.id, merged, doc.metadata ?? {});
          setSavedMessage(`Saved ${key.replace(/_/g, " ")}.`);
          await loadDocument();
        }, "Saving annotation…");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to save");
      }
    },
    [document, field, loadDocument, timer, withLoadingModal]
  );

  function setOverrideMode(v: boolean) {
    setOverrideModeState(v);
    if (v) {
      setOverrideSelection(getEffectiveAiLabels(document?.ai_scorecard ?? null, field));
      setOverrideFocusIndex(0);
    }
  }

  function confirmOverride() {
    const aiLabels = getEffectiveAiLabels(document?.ai_scorecard ?? null, field);
    const selection = overrideSelection;
    setOverrideModeState(false);
    void commit(selection, reviewState(aiLabels, selection));
  }

  function cancelOverride() {
    setOverrideModeState(false);
    setOverrideSelection([]);
  }

  function approve() {
    const aiLabels = getEffectiveAiLabels(document?.ai_scorecard ?? null, field);
    if (aiLabels.length === 0) return;
    void commit(aiLabels, "ai_approved");
  }

  function markEmpty() {
    const aiLabels = getEffectiveAiLabels(document?.ai_scorecard ?? null, field);
    void commit([], reviewState(aiLabels, []));
  }

  return {
    document,
    field,
    remaining: gains.length,
    loading,
    error,
    saving,
    savedMessage,
    override: {
      mode: overrideMode,
      focusIndex: overrideFocusIndex,
      selection: overrideSelection,
      onFocusChange: setOverrideFocusIndex,
      onSelectionChange: setOverrideSelection,
      onConfirm: confirmOverride,
      onCancel: cancelOverride,
    },
    openOverride() {
      setOverrideMode(true);
    },
    approve,
    markEmpty,
  };
}
